import { showDeleteConfirmation } from '@modules/common';
import { switchMap, map } from 'rxjs/operators';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { handleMessage, requestMessage } from '@business/messages';
import { RegionAPI } from '@business/api/region';
import { RegionGeoJson, RegionId, RegionPayload, RegionResponse } from '@business/entities/region';
import { R } from '@core/utils/r';

export class RegionBloc extends BaseBloc {
  private readonly regionsRepo = new Repository<RegionResponse>({
    getItemId: region => region.id,
  });

  readonly regions$ = this.regionsRepo.items$.pipe(map(regions => R.sortBy(regions, r => r.name.toLowerCase())));

  onReset() {
    this.regionsRepo.reset();
  }

  fetchRegions = () => {
    return RegionAPI.fetchRegions().pipe(this.regionsRepo.ops.reset(), handleMessage({ error: requestMessage('fetch_regions_error') }));
  };

  createRegion = (data: RegionPayload) => {
    return RegionAPI.createRegion(data).pipe(
      this.regionsRepo.ops.addOne(item => item),
      handleMessage({ type: requestMessage('create_region') }),
    );
  };

  updateRegion = (id: RegionId, data: RegionPayload) => {
    return RegionAPI.updateRegion(id, data).pipe(
      this.regionsRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ type: requestMessage('update_region') }),
    );
  };

  updateRegionBoundary = (id: RegionId, geoJson: RegionGeoJson | null) => {
    return RegionAPI.updateRegionBoundary(id, geoJson).pipe(
      this.regionsRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ type: requestMessage('update_region_boundary') }),
    );
  };

  deleteRegion = (id: RegionId) => {
    return showDeleteConfirmation(
      'Delete Territory',
      'Do you really want to delete this territory? Its representatives will be left without it, and its open deals will be listed under "Deals needing a territory" on the Sales Map.',
    ).pipe(
      switchMap(() => RegionAPI.deleteRegion(id)),
      this.regionsRepo.ops.removeOne(() => id),
      handleMessage({ type: requestMessage('delete_region') }),
    );
  };
}
